<template>
  <div class="bg-white rounded-xl border border-gray-200 p-5">
    <div class="flex items-start justify-between mb-4">
      <div>
        <h3 class="text-sm font-semibold text-gray-700">Staff Performance</h3>
        <p class="text-xs text-gray-400 mt-0.5">Individual tester metrics including jobs, meters, and completion rates</p>
      </div>
      <button
        @click="exportCsv"
        class="px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors flex-shrink-0"
      >
        <i class="fa-solid fa-arrow-up-from-bracket mr-1.5"></i>Export CSV
      </button>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
            <th class="py-3 pr-4 cursor-pointer select-none" @click="sortStaff('name')">
              Tester <i v-if="staffSort.key === 'name'" :class="staffSort.asc ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-1 text-gray-400"></i>
            </th>
            <th class="py-3 pr-4 cursor-pointer select-none text-right" @click="sortStaff('jobs')">
              Jobs <i v-if="staffSort.key === 'jobs'" :class="staffSort.asc ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-1 text-gray-400"></i>
            </th>
            <th class="py-3 pr-4 cursor-pointer select-none text-right" @click="sortStaff('meters')">
              Meters <i v-if="staffSort.key === 'meters'" :class="staffSort.asc ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-1 text-gray-400"></i>
            </th>
            <th class="py-3 pr-4 text-right">Completed</th>
            <th class="py-3 pr-4 text-right">Cancelled</th>
            <th class="py-3 text-right" style="min-width:160px">Completion Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in sortedStaff" :key="t.name" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
            <td class="py-3 pr-4 font-medium text-gray-800">{{ t.name }}</td>
            <td class="py-3 pr-4 text-right text-gray-600">{{ t.jobs }}</td>
            <td class="py-3 pr-4 text-right text-gray-600">{{ t.meters.toLocaleString() }}</td>
            <td class="py-3 pr-4 text-right text-gray-600">{{ t.completed }}</td>
            <td class="py-3 pr-4 text-right text-gray-600">{{ t.cancelled }}</td>
            <td class="py-3">
              <div class="flex items-center justify-end gap-2">
                <span class="text-xs font-medium" :class="t.rate >= 80 ? 'text-green-600' : t.rate >= 50 ? 'text-yellow-600' : 'text-red-600'">{{ t.rate }}%</span>
                <div class="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div class="h-full rounded-full" :class="t.rate >= 80 ? 'bg-green-500' : t.rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'" :style="{ width: t.rate + '%' }"></div>
                </div>
              </div>
            </td>
          </tr>
          <tr v-if="!sortedStaff.length">
            <td colspan="6" class="py-8 text-center text-gray-400 text-sm">No staff data for this period</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    testers: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      staffSort: { key: "jobs", asc: false },
    };
  },
  computed: {
    sortedStaff() {
      const rows = this.testers.map(t => ({
        name: t.name,
        jobs: t.jobs,
        meters: t.meters || 0,
        completed: (t.statuses && t.statuses["COMPLETED"]) || 0,
        cancelled: (t.statuses && t.statuses["CANCELLED"]) || 0,
        rate: t.jobs ? Math.round((((t.statuses && t.statuses["COMPLETED"]) || 0) / t.jobs) * 100) : 0,
      }));
      const key = this.staffSort.key;
      const dir = this.staffSort.asc ? 1 : -1;
      return rows.sort((a, b) => {
        if (key === "name") return dir * a.name.localeCompare(b.name);
        return dir * ((a[key] || 0) - (b[key] || 0));
      });
    },
  },
  methods: {
    sortStaff(key) {
      if (this.staffSort.key === key) {
        this.staffSort.asc = !this.staffSort.asc;
      } else {
        this.staffSort.key = key;
        this.staffSort.asc = key === "name";
      }
    },
    exportCsv() {
      const rows = this.sortedStaff;
      if (!rows.length) return;
      const header = "Tester,Jobs,Meters,Completed,Cancelled,Completion Rate";
      const lines = rows.map(r => `"${r.name}",${r.jobs},${r.meters},${r.completed},${r.cancelled},${r.rate}%`);
      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "staff-performance-" + this.$moment().format("YYYY-MM-DD") + ".csv";
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
